import { t } from "@lingui/core/macro";
import { useConfirmCustomer2FA } from "@/lib/api-client/paths/customer";
import { Divider, Loader, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import { useEffect } from "react";
import {
  emailValid,
  fullPhoneNumberToNumberAndCountryCode,
} from "../../../../Utilities";
import { apiUpdateCustomer } from "../../../../apiCalls";
import { useCustomers } from "../../../../hooks/useCustomers";
import { useFormFields } from "../../../../hooks/useFormFields";
import useUserPermissionsStore from "../../../../stores/useUserPermissionsStore";
import Analytics from "./Analytics/Analytics";
import CustomerAppointments from "./CustomerAppointmentsList/CustomerAppointments";
import EditingCustomerForm from "./EditingCustomerForm";
import { returnCurrencySymbol } from "../../../../../../shared/utils/utils";
import { api } from "@/lib/api-client";
import { addToast, Button } from "@heroui/react";
import { Tab, Tabs } from "@heroui/react";
import { CustomerFiles } from "./CustomerFiles";
import useFeature from "@/hooks/useFeature";
import { LimeTextarea } from "@/Components/NextBase/LimeTextarea";
import { Trans } from "@lingui/react/macro";

import { CustomerSubscriptions } from "../CustomerPage/components/Subscriptions/CustomerSubscriptions";

const CustomerCard = ({ customerId, handleClose }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const { deleteCustomer, setCustomerData, customerData, refreshCustomerData } =
    useCustomers();

  const { mutateAsync: confirmCustomer2FA } = useConfirmCustomer2FA();

  const [fields, setValues, _, setFields] = useFormFields({});

  const { isFeatureEnabled } = useFeature();

  const permissions = useUserPermissionsStore((state) => state.permissions);

  const { data: currency } = api.values.useGetCurrency({});

  const currencySymbol = returnCurrencySymbol({ currency: currency || "EUR" });

  useEffect(() => {
    async function fetchCustomerData() {
      refreshCustomerData(customerId);
    }
    if (customerId) {
      fetchCustomerData();
    }
  }, [customerId]);

  const deleteCustomerHandler = async () => {
    await deleteCustomer(customerData.customerId);
    handleClose({ isCustomerDeleted: true });
  };

  const submitCustomerFormHandler = async () => {
    const token = localStorage.getItem("usr_token");
    const user = { ...fields };

    if (user.email && !emailValid(user.email)) {
      addToast({
        title: t`Nepravilen elektronski naslov`,
        description: t`Elektronski naslov ni pravilen.`,
        color: "danger",
      });
      return;
    }

    if (user.gsm != null) {
      const phoneNumber = fullPhoneNumberToNumberAndCountryCode(user.gsm);

      user.gsm = phoneNumber.gsm;
      user.countryCode = phoneNumber?.countryCode ?? "386";
    }

    const { shouldOverridePaymentPercentage } = user;
    delete user.shouldOverridePaymentPercentage;

    const cData = { ...customerData };
    delete cData.upcommingAppointments;
    delete cData.completedAppointments;

    const response = await apiUpdateCustomer(
      {
        ...cData,
        ...user,
        birthday: user.birthday
          ? dayjs(user.birthday).format("YYYY-MM-DD")
          : null,
        customerId: customerData.customerId,
        notes: user.notes ?? "",
        language: user.language,
        formPaymentRequirement: user.formPaymentRequirement,
        formPaymentPercentageOverride:
          shouldOverridePaymentPercentage &&
          user.formPaymentRequirement === "always"
            ? user.formPaymentPercentageOverride
            : null,
      },
      token,
    );

    await refreshCustomerData(customerData.customerId);

    // if (response.status === 200) {
    //   setCustomerData(() => ({
    //     ...customerData,
    //     ...user,
    //     customerId: customerData.customerId,
    //   }));
    // }
  };

  const handleConfirmGsmEmailClick = async (type) => {
    modals.openConfirmModal({
      title: t`Ali ste prepričani, da želite potrditi ${type} za 2FA sistem?`,
      labels: { confirm: t`Potrdi`, cancel: t`Prekliči` },
      onConfirm: async () => {
        const response = await confirmCustomer2FA({
          customerId: customerData.customerId,
          body: {
            confirmEmail: type === "email",
            confirmGsm: type === "gsm",
          },
        });
        if (response.message === "OK") {
          notifications.show({
            title: t`Uspešno potrjeno`,
            message:
              type === "gsm"
                ? t`Telefonska številka je bila uspešno potrjena.`
                : t`Elektronski naslov je bil uspešno potrjen.`,
          });
        }
        refreshCustomerData(customerId);
      },
      withinPortal: true,
      zIndex: 10000,
      centered: true,
    });
  };

  if (!customerData) {
    return <Loader />;
  }

  return (
    <div className="flex min-h-full flex-col justify-between overflow-y-auto md:flex-row">
      <div id="left-side" className="w-full md:w-[70%]">
        <Tabs disableAnimation={true} variant="underlined">
          <Tab key={"info"} title={t`Oseba`}>
            <div className="overflow-y-auto md:max-h-[calc(100vh-100px)]">
              <EditingCustomerForm
                submitCustomerFormHandler={submitCustomerFormHandler}
                fields={fields}
                setFields={setFields}
                setValues={setValues}
                isMobile={isMobile}
                customerData={customerData}
                handleDeleteCustomer={deleteCustomerHandler}
                openConfirmGsmEmailClick={handleConfirmGsmEmailClick}
              />

              <div className="mt-4 flex flex-col items-end text-sm text-default-500">
                <LimeTextarea
                  size="sm"
                  value={fields.notes}
                  onValueChange={(value) => setValues(value, "notes")}
                  label={t`Komentar`}
                />
                <Button
                  size="sm"
                  color="primary"
                  onPress={submitCustomerFormHandler}
                  className="mt-1"
                >
                  <Trans>Shrani</Trans>
                </Button>
              </div>

              {permissions.view_analytics ? (
                <Analytics
                  currencySymbol={currencySymbol}
                  completedAppointments={
                    customerData.completedAppointments || []
                  }
                  isMobile={isMobile}
                  currency={customerData.mainCurrency ?? "EUR"}
                />
              ) : null}
            </div>
          </Tab>
          {isFeatureEnabled("CUSTOMER_FILES") && (
            <Tab key={"files"} title={t`Datoteke`}>
              <div className="overflow-y-auto md:max-h-[calc(100vh-100px)]">
                <CustomerFiles customerId={customerId} />
              </div>
            </Tab>
          )}

          {isFeatureEnabled("SUBSCRIPTIONS") && (
            <Tab
              data-identifier="subscriptions-tab"
              key={"subscriptions"}
              title={t`Naročnine`}
            >
              <div className="overflow-y-auto md:max-h-[calc(100vh-100px)]">
                <CustomerSubscriptions customerId={Number(customerId)} />
              </div>
            </Tab>
          )}
        </Tabs>
      </div>
      <Divider orientation={"vertical"} mx={"md"} />
      <CustomerAppointments
        currencySymbol={currencySymbol}
        refreshCustomerData={refreshCustomerData}
        setCustomerData={setCustomerData}
        customerData={customerData}
        isMobile={isMobile}
      />
    </div>
  );
};

export const IconsContainer = ({ children }) => (
  <div className="flex items-center gap-4">{children}</div>
);

export const Details = ({ children }) => (
  <div className="mb-4 flex flex-col gap-2">{children}</div>
);

export default CustomerCard;
